import { forwardRef, useEffect, useState } from "react";
import RCSlider, { SliderProps as RCSliderProps } from "rc-slider";
import "rc-slider/assets/index.css";

import { cn } from "lib/utils";
import styles from "./slider.module.css";
import { formatCurrency } from "lib/utils";

interface CustomHandleProps extends React.HTMLAttributes<HTMLDivElement> {
  style?: React.CSSProperties;
  value?: number;
  index?: number;
  disabled?: boolean;
}

function CustomHandle({
  style,
  value = 0,
  disabled = false,
  isVisible,
  ...restProps
}: CustomHandleProps & { isVisible: boolean }) {
  const mergedTransform = style?.transform
    ? `${style.transform} translateY(-14px)`
    : "translateY(-14px)";

  const mergedStyle: React.CSSProperties = {
    ...style,
    transform: mergedTransform,
  };

  return (
    <div className={styles.relative}>
      <div
        style={mergedStyle}
        {...restProps}
        className={cn(styles.handle, disabled && styles.handleDisabled)}
      >
        <div
          className={cn(
            styles.tooltip,
            isVisible ? styles.tooltipVisible : styles.tooltipHidden,
          )}
        >
          {formatCurrency(value)}
        </div>
      </div>
    </div>
  );
}

export interface SliderProps extends Omit<RCSliderProps, "onChange"> {
  value?: number[];
  onValueChange?: (newValue: number[]) => void;
}

export const Slider = forwardRef<HTMLDivElement, SliderProps>(
  (
    {
      className,
      value = [0],
      min = 0,
      max = 100,
      step = 1,
      onValueChange,
      ...props
    },
    ref,
  ) => {
    const [sliderValue, setSliderValue] = useState<number>(value[0] ?? 0);
    const [percentage, setPercentage] = useState(0);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
      if (Array.isArray(value)) {
        setSliderValue(value[0] ?? min);
      } else if (typeof value === "number") {
        setSliderValue(value);
      }
    }, [value, min]);

    useEffect(() => {
      const clampedVal = Math.max(Math.min(sliderValue, max), min);
      const pct = ((clampedVal - min) / (max - min)) * 100;
      setPercentage(pct);
    }, [sliderValue, min, max]);

    const handleChange = (val: number | number[]) => {
      const newVal = Array.isArray(val) ? val[0] : val;
      setSliderValue(newVal);
      onValueChange?.([newVal]);
    };

    const getBgStyle = (percentage: number, threshold: number) => {
      return percentage >= threshold
        ? "rgb(var(--primary-80))"
        : "rgb(var(--neutral-100))";
    };

    return (
      <div
        ref={ref}
        className={cn(styles.sliderContainer, className)}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        onFocus={() => setIsVisible(true)}
        onBlur={() => setIsVisible(false)}
      >
        <div className={styles.sliderWrapper}>
          <div className={styles.progressMarkers}>
            {[0, 25, 50, 75, 100].map((threshold) => (
              <span
                key={threshold}
                className={cn(
                  styles.progressMarker,
                  (threshold === 25 || threshold === 75) &&
                    styles.progressMarkerHidden,
                )}
                style={{
                  backgroundColor: getBgStyle(percentage, threshold),
                }}
              />
            ))}
          </div>

          <RCSlider
            min={min}
            max={max}
            step={step}
            value={sliderValue}
            onChange={handleChange}
            handleRender={(origin, handleProps) => {
              const originElement = origin as React.ReactElement;
              const { style: originStyle, ...restOriginProps } =
                originElement.props;
              const { value } = handleProps;

              return (
                <CustomHandle
                  style={originStyle}
                  {...restOriginProps}
                  value={value}
                  isVisible={isVisible || handleProps.dragging}
                />
              );
            }}
            styles={{
              track: {
                backgroundColor: "rgb(var(--primary-80))",
                height: 8,
                borderRadius: 9999,
              },
              rail: {
                backgroundColor: "rgb(var(--neutral-100))",
                height: 8,
                borderRadius: 9999,
              },
            }}
            {...props}
          />
        </div>
      </div>
    );
  },
);

Slider.displayName = "Slider";
